import type { Severity, VulnerabilityFinding } from '@bartizo/shared';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { getServerVulnerabilities } from '../api/servers.js';
import { formatInstant, formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { DataTable, type Column } from '../ui/DataTable.js';

/**
 * A server's packages with known vulnerabilities (P11.8.8), from the advisory
 * data central keeps for its release (Canonical's for Ubuntu, Debian's for
 * Debian, both as OSV publishes them), and the command that updates them — to
 * copy and run on the server; Bartizo runs nothing there.
 */

const SEVERITY_BADGE: Readonly<Record<Severity, string>> = {
  critical: 'badge-danger',
  high: 'badge-danger',
  medium: 'badge-warning',
  low: 'badge-muted',
  info: 'badge-muted',
};
const SEVERITY_ORDER: readonly Severity[] = ['critical', 'high', 'medium', 'low', 'info'];

type Shown = 'fixable' | 'all';

/** Where a person reads the advisory: the distribution's own page. */
function advisoryLink(finding: VulnerabilityFinding): string {
  const cve = finding.cves[0];
  if (finding.advisoryId.startsWith('UBUNTU-') && cve !== undefined) {
    return `https://ubuntu.com/security/${cve}`;
  }
  if (finding.advisoryId.startsWith('DEBIAN-') && cve !== undefined) {
    return `https://security-tracker.debian.org/tracker/${cve}`;
  }
  return `https://osv.dev/vulnerability/${encodeURIComponent(finding.advisoryId)}`;
}

export function VulnerabilitiesTab({
  serverId,
  timezone,
}: {
  serverId: string;
  timezone: string;
}): ReactNode {
  const [shown, setShown] = useState<Shown>('fixable');
  const vulnerabilities = useQuery({
    queryKey: ['vulnerabilities', serverId],
    queryFn: () => getServerVulnerabilities(serverId),
    refetchInterval: 5 * 60_000,
  });

  if (vulnerabilities.isPending) {
    return <div className="skeleton skeleton-block" aria-busy="true" aria-label="Loading" />;
  }
  if (vulnerabilities.isError) {
    return (
      <Alert tone="danger" title="Could not load the vulnerabilities">
        {vulnerabilities.error instanceof Error
          ? vulnerabilities.error.message
          : 'Try again in a moment.'}
      </Alert>
    );
  }
  const data = vulnerabilities.data;
  if (data.ecosystem === null) {
    return (
      <Card title="Vulnerabilities">
        <p className="muted text-sm">
          Bartizo matches Ubuntu LTS and Debian packages against their distributions&apos;
          advisories. This server&apos;s system is not one of them, or its inventory has not been
          read yet.
        </p>
      </Card>
    );
  }
  if (data.advisoriesAt === null || data.matchedAt === null) {
    return (
      <Card title="Vulnerabilities">
        <p className="muted text-sm">
          Not matched yet. The central server downloads the advisories for{' '}
          {data.ecosystem.replace(/:/g, ' ')} once a day and matches each server&apos;s packages
          against them within minutes of that, and of each inventory read.
        </p>
      </Card>
    );
  }

  const fixable = data.findings.filter((each) => each.fixedVersion !== null);
  const rows = shown === 'fixable' ? fixable : data.findings;
  const counts = SEVERITY_ORDER.map(
    (severity) => [severity, fixable.filter((each) => each.severity === severity).length] as const,
  ).filter(([, n]) => n > 0);

  const columns: Column<VulnerabilityFinding>[] = [
    {
      key: 'severity',
      header: 'Severity',
      cell: (row) => (
        <span className={`badge ${SEVERITY_BADGE[row.severity]}`}>{row.severity}</span>
      ),
      sortValue: (row) => SEVERITY_ORDER.indexOf(row.severity),
    },
    {
      key: 'advisory',
      header: 'Vulnerability',
      rowHeader: true,
      cell: (row) => (
        <>
          <a href={advisoryLink(row)} target="_blank" rel="noreferrer noopener">
            {row.cves[0] ?? row.advisoryId}
          </a>
          {row.cvssScore !== null && <span className="cell-sub">CVSS {row.cvssScore}</span>}
          {row.summary !== null && <span className="cell-sub">{row.summary}</span>}
        </>
      ),
      sortValue: (row) => row.cves[0] ?? row.advisoryId,
    },
    {
      key: 'package',
      header: 'Package',
      cell: (row) => (
        <>
          {row.package}
          {row.source !== null && <span className="cell-sub">from {row.source}</span>}
        </>
      ),
      sortValue: (row) => row.package,
    },
    {
      key: 'installed',
      header: 'Installed',
      wideOnly: true,
      cell: (row) => <span className="mono">{row.installedVersion}</span>,
    },
    {
      key: 'fixed',
      header: 'Fixed in',
      cell: (row) =>
        row.fixedVersion === null ? (
          <span className="muted">no fix yet</span>
        ) : (
          <>
            <span className="mono">{row.fixedVersion}</span>
            {row.needsPro && <span className="cell-sub">with Ubuntu Pro</span>}
          </>
        ),
      sortValue: (row) => (row.fixedVersion === null ? 1 : 0),
    },
    {
      key: 'since',
      header: 'Seen since',
      wideOnly: true,
      cell: (row) => (
        <span title={formatInstant(row.firstSeenAt, { timezone })}>
          {formatRelative(row.firstSeenAt)}
        </span>
      ),
      sortValue: (row) => row.firstSeenAt,
    },
  ];

  return (
    <>
      <Card
        title="Vulnerabilities"
        description={`${data.ecosystem.replace(/:/g, ' ')} advisories from ${formatRelative(data.advisoriesAt)}, matched ${formatRelative(data.matchedAt)}. ${String(data.total)} in all, ${String(fixable.length)} with a fix.`}
        aside={
          counts.length === 0 ? (
            <span className="badge badge-success">Nothing to update</span>
          ) : (
            <span className="row">
              {counts.map(([severity, n]) => (
                <span key={severity} className={`badge ${SEVERITY_BADGE[severity]}`}>
                  {n} {severity}
                </span>
              ))}
            </span>
          )
        }
      >
        {data.upgradeCommand !== null && <UpgradeCommand command={data.upgradeCommand} />}
        <div className="segmented" role="group" aria-label="Which vulnerabilities">
          <button
            type="button"
            aria-pressed={shown === 'fixable'}
            onClick={() => {
              setShown('fixable');
            }}
          >
            With a fix
          </button>
          <button
            type="button"
            aria-pressed={shown === 'all'}
            onClick={() => {
              setShown('all');
            }}
          >
            All
          </button>
        </div>
        <DataTable
          caption="Vulnerable packages"
          columns={columns}
          rows={rows}
          rowKey={(row) => `${row.advisoryId}|${row.package}`}
          pagination={{}}
          empty={
            <p className="muted text-sm">
              {shown === 'fixable'
                ? 'No vulnerability here has a fix to install.'
                : 'No known vulnerability in its packages.'}
            </p>
          }
        />
      </Card>
    </>
  );
}

/** The command to run on the server, to copy: Bartizo runs nothing there. */
function UpgradeCommand({ command }: { command: string }): ReactNode {
  const [copied, setCopied] = useState(false);
  return (
    <section className="stack">
      <p className="text-sm">
        Run this on the server to install every fix that needs no Ubuntu Pro subscription:
      </p>
      <pre className="mono text-sm code-block">{command}</pre>
      <p className="text-sm muted">
        {/linux-(?:image|headers)-/.test(command) &&
          'A kernel fix takes effect after a reboot, and the old kernel stays installed until apt autoremove. '}
        apt only sees the fixes in the archives this server uses. If it says a package is already
        the newest version, what remains is a fix only Ubuntu Pro carries, marked on its row.
      </p>
      <div className="row">
        <Button
          onClick={() => {
            void navigator.clipboard
              .writeText(command)
              .then(() => {
                setCopied(true);
              })
              .catch(() => undefined);
          }}
        >
          {copied ? 'Copied' : 'Copy the command'}
        </Button>
      </div>
    </section>
  );
}
